import MarketPrice from "@/common/components/marketPrice";
import { H3 } from "@/common/elements/headings";
import {
  getLogisticCompaniesDetails,
  getReceivingCompanyOptions,
  packageType,
  temperatureOption,
  vehicleOptions,
} from "../data";
import useQuery from "@/hooks/useQuery";
import { LOGISTICS_COMPANIES_URL } from "@/common/constants";

import { useState } from "react";
import { KeyValueObj } from "@/common/types";
import { Select } from "@/common/components/selectInput";
import Input from "@/common/components/Input/Input";
import { AddressManager } from "@/common/components/addresses";
import Form from "@/common/components/form/Form";
import { Button } from "@/common/components/button";
import { getNewDeliveryAddressFormInputs } from "@/common/components/addresses/data";

import { MultiSelect } from "@/common/components/multi-selectInput";
import { statesOnly } from "@/common/data/statesOnly";
interface IProps {
  formData: KeyValueObj;
  onContinue: () => void;
  onBack: () => void;
  onChange?: (name: keyof KeyValueObj, value: string) => void;
  setFormData: React.Dispatch<React.SetStateAction<KeyValueObj>>;
  productDetails: KeyValueObj;
}

export default function DeliveryInfo({
  formData,
  setFormData,
  onContinue,
  onBack,
  productDetails,
}: IProps) {
  const [senderAddressType, setSenderAddressType] = useState("company_address");
  const [errorMessages] = useState<string[]>([]);
  const [stateValues, setStateValues] = useState<KeyValueObj>({});
  const { data: logisticsCompanies } = useQuery(
    LOGISTICS_COMPANIES_URL + `?state=${Object?.values(stateValues)[0]}`,
  );

  const handleUpdateLogistic = (name: string, value: (string | number)[]) => {
    if (!Array.isArray(value) || value.length === 0) {
      setFormData((prev) => ({
        ...prev,
        [name]: [],
      }));
      return;
    }

    const selectedCompanies = logisticsCompanies.filter(
      (company: KeyValueObj) => value.includes(company.id),
    );

    setFormData((prev) => ({
      ...prev,
      [name]: Array.isArray(selectedCompanies)
        ? selectedCompanies
        : [selectedCompanies],
    }));
  };

  function setDropOffAddress(_: (prev: any) => any): void {
    throw new Error("Function not implemented.");
  }

  const requiredFields = [
    "logistics_company",
    "vehicle_needed",
    "expected_delivery_date",
    "recipent_phone_number",
    "sender_phone_number",
    "pickup_option",
  ];

  const isFormValid = requiredFields.every((field) => {
    const value = formData[field];

    // For array or object fields
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "object" && value !== null)
      return Object.keys(value).length > 0;

    // For strings/numbers
    return Boolean(value && value !== "");
  });

  const stateOptions = statesOnly.map((item) => ({
    id: item.id,
    title: item.name,
  }));

  return (
    <>
      <div className="w-full p-1.5 gap-10 ">
        <div className="my-10">
          <H3 className="my-3">Logistics Provider Details</H3>
          <MarketPrice />

          <div className="my-5">
            <div className="flex gap-5">
              <Select
                name="package_type"
                options={packageType}
                placeholder={
                  productDetails?.tertiary_packaging ||
                  productDetails?.secondary_packaging ||
                  productDetails?.primary_packaging
                }
                initialValue={
                  productDetails?.tertiary_packaging ||
                  productDetails?.secondary_packaging ||
                  productDetails?.primary_packaging
                }
                prompt="Packaging Type"
                fullWidth={true}
                shortWidth={false}
                indexLevel={10}
                formValues={formData}
                setFormValues={setFormData}
                readOnly={true}
              />
              <Select
                name="category"
                options={temperatureOption}
                placeholder={productDetails?.storage_requirement}
                initialValue={productDetails?.storage_requirement}
                prompt="Temperature Categories"
                fullWidth={true}
                shortWidth={false}
                indexLevel={9}
                formValues={formData}
                setFormValues={setFormData}
                readOnly={true}
              />
            </div>

            {productDetails?.storage_requirement === "cold storage" && (
              <div className="flex gap-5 mt-5 ">
                <Input
                  placeholder={"18"}
                  inputType={"number"}
                  prompt={"Minimum Temperature('C)"}
                  fullWidth={false}
                  shortWidth={false}
                  name={"min_temperature"}
                  indexLevel={8}
                  formValues={formData}
                  setFormValues={setFormData}
                  updateData={(name: string, value: any) =>
                    setFormData((prev: any) => ({
                      ...prev,
                      [name]: value,
                    }))
                  }
                  returnId
                />

                <Input
                  placeholder="30cm"
                  prompt={"Maximum Temperature('C)"}
                  fullWidth={false}
                  shortWidth={false}
                  inputType={"number"}
                  name={"max_temperature"}
                  indexLevel={7}
                  formValues={formData}
                  setFormValues={setFormData}
                  updateData={(name: string, value: any) =>
                    setFormData((prev) => ({ ...prev, [name]: value }))
                  }
                />
              </div>
            )}

            <div className="flex gap-5 mt-8">
              <Input
                placeholder={"22cm"}
                prompt={"Product Length (cm)"}
                fullWidth={false}
                shortWidth={false}
                name={"length"}
                inputType={"number"}
                indexLevel={6}
                formValues={formData?.length}
                setFormValues={setFormData}
                updateData={(name: string, value: any) =>
                  setFormData((prev: any) => ({
                    ...prev,
                    [name]: value,
                  }))
                }
                returnId
              />

              <Input
                placeholder="30cm"
                prompt="Product height (cm)"
                fullWidth={false}
                shortWidth={false}
                name={"height"}
                inputType={"number"}
                indexLevel={5}
                formValues={formData.height}
                setFormValues={setFormData}
                updateData={(name: string, value: any) =>
                  setFormData((prev) => ({ ...prev, [name]: value }))
                }
              />
              <Input
                placeholder="30cm"
                prompt="Product width (cm)"
                fullWidth={false}
                shortWidth={false}
                name={"width"}
                inputType={"number"}
                indexLevel={5}
                formValues={formData?.width}
                setFormValues={setFormData}
                updateData={(name: string, value: any) =>
                  setFormData((prev) => ({ ...prev, [name]: value }))
                }
              />
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-2 justify-center">
          <div className="w-[20rem]">
            <Select
              options={stateOptions}
              placeholder={"select a specific state"}
              prompt={"Filter By States "}
              name={""}
              fullWidth={true}
              shortWidth={false}
              indexLevel={3}
              formValues={stateValues}
              setFormValues={setStateValues}
              updateData={(name, value) => {
                setStateValues((prev) => ({
                  ...prev,
                  [name]: value,
                }));
              }}
            />
          </div>

          <MultiSelect
            options={getReceivingCompanyOptions(logisticsCompanies)}
            placeholder="Select Logistics Companies"
            updateData={handleUpdateLogistic}
            handleSearch={() => {}}
            prompt="Logistics Company"
            fullWidth
            shortWidth
            name="logistics_company"
            indexLevel={2}
            formValues={formData}
            setFormValues={setFormData}
            returnId
            required
          />
        </div>

        <div className="mt-10 w-full">
          <H3 className="my-5">Other Details</H3>
          <div className="flex gap-5">
            <Select
              name="vehicle_needed"
              options={vehicleOptions}
              placeholder="Bus"
              prompt="Preferred Vehicle Type"
              fullWidth={false}
              shortWidth={false}
              indexLevel={1}
              formValues={formData}
              setFormValues={setFormData}
              updateData={(name, value) =>
                setFormData((prev: any) => ({ ...prev, [name]: value }))
              }
              returnId
            />

            <Input
              placeholder="DD-MM-YYYY"
              prompt="Expected Delivery Date"
              fullWidth={false}
              shortWidth={false}
              name="expected_delivery_date"
              inputType="date"
              rules={{
                notBefore: {
                  expectedValue: new Date(
                    Date.now() + 24 * 60 * 60 * 1000,
                  ).toISOString(),
                  errorMessage: "Date cannot be in the past",
                },
              }}
              indexLevel={100}
              formValues={formData}
              setFormValues={setFormData}
              required
              updateData={(name: string, value: any) =>
                setFormData((prev: any) => ({ ...prev, [name]: value }))
              }
              returnId
            />
          </div>

          <div className="flex gap-5 mt-8">
            <Input
              placeholder={"07012345678"}
              prompt={"Recipent's Secondary Phone Number"}
              fullWidth={false}
              shortWidth={false}
              name={"recipent_phone_number"}
              indexLevel={98}
              formValues={formData}
              setFormValues={formData?.recipent_phone_number}
              updateData={(name: string, value: any) =>
                setFormData((prev: any) => ({
                  ...prev,
                  [name]: value,
                }))
              }
              returnId
            />

            <Input
              placeholder="09012345678"
              prompt="Sender's Secondary Phone Number"
              fullWidth={false}
              shortWidth={false}
              name="sender_phone_number"
              indexLevel={97}
              setFormValues={setFormData}
              formValues={formData}
              updateData={(name: string, value: any) =>
                setFormData((prev) => ({ ...prev, [name]: value }))
              }
            />
          </div>
        </div>

        <div className="my-5"></div>

        <H3>Pick-up Type</H3>

        <div className="grid grid-cols-2 gap-6 mt-4">
          <span className="radio-wrapper ">
            <input
              type="radio"
              value="logistics_pickup"
              name="pickup_option"
              id="logistics-pickup"
              checked={formData?.pickup_option == "logistics_pickup"}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  pickup_option: e.target.value,
                }))
              }
            />
            <label htmlFor="logistics-pickup" className="mx-2">
              Logistic Pickup - The logistic company will come to either your
              office address or an address you will specify.
            </label>
          </span>
          <span className="radio-wrapper">
            <input
              type="radio"
              value="sender_drop_off"
              name="pickup_option"
              checked={formData?.pickup_option === "sender_drop_off"}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  pickup_option: e.target.value,
                }))
              }
            />
            <label htmlFor="sender_drop_off" className="mx-2">
              Sender Dropoff - You will take the items to the logistics
              company's address
            </label>
          </span>
        </div>

        {formData?.pickup_option === "logistics_pickup" ? (
          <div className="my-5">
            <H3>Where should the logistic company pick up the items</H3>
            <div className="flex justify-between">
              <span className="">
                <input
                  type="radio"
                  value="company_address"
                  name="sender_address_type"
                  id="company_address"
                  checked={senderAddressType === "company_address"}
                  onChange={(e) => setSenderAddressType(e.target.value)}
                />

                <label htmlFor="company_address" className="mx-2">
                  Company Address
                </label>
              </span>
              <span className="radio-wrapper">
                <input
                  type="radio"
                  value="custom_address"
                  name="sender_address_type"
                  id="custom_address"
                  checked={senderAddressType === "custom_address"}
                  onChange={(e) => setSenderAddressType(e.target.value)}
                />

                <label htmlFor="custom_address" className="mx-2">
                  Custom Address
                </label>
              </span>
            </div>
          </div>
        ) : (
          <div className="my-5">
            <H3>Logistic Provider Address</H3>
            <div className="">
              {getLogisticCompaniesDetails(formData?.logistics_company)?.map(
                (company: KeyValueObj, i: number) => (
                  <div key={company.id} className="p-4 border rounded mb-3">
                    <h1 key={i}>{company[0]?.value}</h1>
                    <h1 key={i}>{company[1]?.value}</h1>
                  </div>
                ),
              )}
            </div>
          </div>
        )}

        {formData?.pickup_option === "logistics_pickup" &&
        senderAddressType === "custom_address" ? (
          <Form
            formInputs={getNewDeliveryAddressFormInputs()}
            processInputs={() => {}}
            removeSubmitButton
            removePadding
            externalUpdateData={(name, value) =>
              setDropOffAddress((prev: any) => ({ ...prev, [name]: value }))
            }
          />
        ) : null}

        {formData?.pickup_option === "logistics_pickup" &&
        senderAddressType === "company_address" ? (
          <AddressManager
            selectedAddress={formData?.pickup_address}
            setSelectedAddress={(address) =>
              setFormData((prev) => ({ ...prev, pickup_address: address }))
            }
          />
        ) : null}
        {errorMessages?.length > 0 ? (
          <div>
            {errorMessages.map((error: string) => (
              <p className="error">{error}</p>
            ))}
          </div>
        ) : null}
        <div className="flex-row  gap-5 mt-10  p-5 pb-20">
          <Button secondary onClick={onBack}>
            Back
          </Button>
          <Button onClick={onContinue} disabled={!isFormValid}>
            {" "}
            Continue
          </Button>
        </div>
      </div>
    </>
  );
}
